import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import CookieBanner from './CookieBanner'
import { IconClose } from './ui/Icon'
import { PRIVACY_I18N } from '../data/privacy-i18n'
import { langFromPath } from '../lib/routes'
import './consent.css'

/**
 * Privacy notice in a native <dialog>: focus is trapped and Escape closes it
 * without any custom key handling.
 *
 * @param {{ lang: 'tr' | 'en' | 'de', open: boolean, onClose: () => void }} props
 */
function PrivacyDialog({ lang, open, onClose }) {
    const t = PRIVACY_I18N[lang] || PRIVACY_I18N.tr
    const ref = useRef(null)

    useEffect(() => {
        const dialog = ref.current
        if (!dialog) return
        if (open && !dialog.open) dialog.showModal()
        if (!open && dialog.open) dialog.close()
    }, [open])

    // A click on the backdrop lands on the <dialog> element itself.
    const onBackdrop = (e) => {
        if (e.target === ref.current) onClose()
    }

    return (
        <dialog
            ref={ref}
            className="privacy-dialog"
            aria-labelledby="privacy-title"
            onClose={onClose}
            onClick={onBackdrop}
        >
            <div className="privacy-sheet">
                <header className="privacy-head">
                    <h2 id="privacy-title">{t.title}</h2>
                    <button type="button" className="privacy-close" onClick={onClose} aria-label={t.close}>
                        <IconClose />
                    </button>
                </header>
                <div className="privacy-body">
                    {t.sections.map((section) => (
                        <section key={section.heading}>
                            <h3>{section.heading}</h3>
                            {section.body ? <p>{section.body}</p> : null}
                            {section.lines ? (
                                <p>
                                    {section.lines.map((line, i) => (
                                        <span key={line}>
                                            {i > 0 ? <br /> : null}
                                            {line}
                                        </span>
                                    ))}
                                </p>
                            ) : null}
                        </section>
                    ))}
                </div>
                <footer className="privacy-foot">
                    <button type="button" className="btn btn--blue" onClick={onClose}>
                        {t.accept}
                    </button>
                </footer>
            </div>
        </dialog>
    )
}

/**
 * Site-wide consent: the cookie banner and the privacy notice it opens.
 * Mounted once in Router so every route — not only the home pages — offers
 * the consent choice before analytics storage is used.
 */
export default function ConsentLayer() {
    const { pathname } = useLocation()
    const lang = langFromPath(pathname)
    const [privacyOpen, setPrivacyOpen] = useState(false)
    const openPrivacy = useCallback(() => setPrivacyOpen(true), [])
    const closePrivacy = useCallback(() => setPrivacyOpen(false), [])

    return (
        <>
            <CookieBanner lang={lang} onOpenPrivacy={openPrivacy} />
            <PrivacyDialog lang={lang} open={privacyOpen} onClose={closePrivacy} />
        </>
    )
}
